import { Autocomplete, Avatar, Box, Button, Chip, Divider, Drawer, Grid, IconButton, Paper, Stack, TextField, Typography } from "@mui/material";
import { BodyDetalhesContainer, DetalhesContainer, HeaderDetalhesContainer } from "../CardPizzaOperacao/styles";
import { useMoeda } from "../../../core/hooks/useMoeda";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ISabor } from "../../interfaces/ISabor";
import { IBorda } from "../../interfaces/IBorda";
import { IProduto } from "../../interfaces/IProduto";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { RealInput } from "../../../core/components/RealInput";
import { IProdutoPedido } from "../../../pedidos/interface/IProdutoPedido";
import { PaperMontagem, PaperResumo } from "./styles";
import { useTamanhoTela } from "../../../core/hooks/useTamanhoTela";
import { IMassa } from "../../interfaces/IMassa";
import { IAdicionalPizza } from "../../interfaces/IAdicionalPizza";
import { ChevronLeft, Add, Remove } from "@mui/icons-material";

interface DrawerPizzaOperacaoProps {
    open: boolean;
    toggleDrawer: () => void;
    produto: IProduto;
    onSubmit: (produto: IProdutoPedido) => void;

    sabores?: ISabor[];
    borda?: IBorda;
    massa?: IMassa;
    observacao?: string;
    valorAdicional?: any;
    uuid?: string;
    adicionaisSelecionados?: IAdicionalPizza[];
}

export const DrawerPizzaOperacao = ({
    valorAdicional: valorAdicionalEdicao,
    observacao: observacaoEdicao,
    sabores: saboresEdicao,
    borda: bordaEdicao,
    massa: massaEdicao,
    adicionaisSelecionados: adicionaisSelecionadosEdicao,
    toggleDrawer,
    onSubmit,
    produto,
    open,
    uuid
}: DrawerPizzaOperacaoProps) => {
    const [sabores, setSabores] = useState<ISabor[]>([]);
    const [massa, setMassa] = useState<IMassa | undefined | null>(null);
    const [borda, setBorda] = useState<IBorda | undefined | null>(null);
    const [observacao, setObservacao] = useState<string | undefined>(undefined);
    const [valorAdicional, setValorAdicional] = useState<any>(undefined);
    const [adicionaisSelecionados, setAdicionaisSelecionados] = useState<IAdicionalPizza[]>([]);
    const { modeloTela } = useTamanhoTela();

    const snackbar = useSnackbar();
    const { real } = useMoeda();

    const handleValorAdicional = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValorAdicional(Number(event.target.value))
    }

    const handleObservacao = (event: React.ChangeEvent<HTMLInputElement>) => {
        setObservacao(event.target.value)
    }

    const handleBordaChange = (event: React.SyntheticEvent<Element, Event>, value: IBorda | undefined | null) => {
        setBorda(value);
    }

    const handleMassaChange = (event: React.SyntheticEvent<Element, Event>, value: IMassa | undefined | null) => {
        setMassa(value);
    }

    const handleSaboresChange = (event: React.SyntheticEvent<Element, Event>, value: ISabor[]) => {
        if (produto.quantidadeSabores) {
            if (value.length > produto.quantidadeSabores) {
                return snackbar(`Você já selecionou os ${produto.quantidadeSabores} sabores`, { severity: "error" })
            }
        }
        setSabores(value);
    }

    const handleToggleDrawer = () => {
        if (!saboresEdicao) {
            setSabores([]);
        }

        if (!valorAdicionalEdicao) {
            setValorAdicional(undefined);
        }

        if (!observacaoEdicao) {
            setObservacao(undefined)
        }

        if (!bordaEdicao) {
            setBorda(null);
        }

        if (!massaEdicao) {
            setMassa(null);
        }

        if (!adicionaisSelecionadosEdicao) {
            setAdicionaisSelecionados([]);
        }

        toggleDrawer();
    }

    const limiteAdicionaisAtingido = useMemo(() => {
        const limite = produto.configuracao?.maxAdicionais;
        return limite !== undefined && adicionaisSelecionados.length >= limite;
    }, [adicionaisSelecionados, produto.configuracao]);

    const handleToggleAdicional = (adicional: IAdicionalPizza) => {
        const jaExiste = adicionaisSelecionados.some(a => a.uuid === adicional.uuid);
        if (jaExiste) {
            setAdicionaisSelecionados(prev => prev.filter(a => a.uuid !== adicional.uuid));
        } else {
            if (limiteAdicionaisAtingido) {
                snackbar(`Limite de ${produto.configuracao?.maxAdicionais} adicionais atingido`, { severity: "warning" });
                return;
            }
            setAdicionaisSelecionados(prev => [...prev, adicional]);
        }
    }

    const valorTotalSabor = useMemo(() => {
        // Precificação por preço dividido
        if (produto.configuracao?.modeloPrecificacao === "precoDividido") {
            let total = 0;
            sabores.forEach(sabor => {
                total += sabor.preco;
            });
            return Math.ceil(total / sabores.length);
        }

        // Precificação pelo maior preço
        if (produto.configuracao?.modeloPrecificacao === "maiorPreco") {
            if (sabores.length === 0) {
                return 0;
            }
            const saborMaisCaro = sabores.reduce((maior, atual) => maior.preco > atual.preco ? maior : atual);
            return saborMaisCaro.preco;
        }

        // Precificação mista
        if (produto.configuracao?.modeloPrecificacao === "precoMisto") {
            // Se a quantidade de sabores for maior que 2, usa a lógica do maior preço
            if (sabores.length > 2) {
                const saborMaisCaro = sabores.reduce((maior, atual) => maior.preco > atual.preco ? maior : atual);
                return saborMaisCaro.preco;
            } else {
                // Se a quantidade de sabores for 2 ou menos, usa a lógica de preço dividido
                let total = 0;
                sabores.forEach(sabor => {
                    total += sabor.preco;
                });
                return Math.ceil(total / sabores.length);
            }
        }

        // Retorna 0 como valor padrão se nenhuma das condições anteriores for verdadeira
        return 0;

    }, [sabores, produto.configuracao]);


    const valorTotalAdicionais = useMemo(() => {
        return adicionaisSelecionados.reduce((acc, a) => acc + a.preco, 0);
    }, [adicionaisSelecionados]);

    const valorTotal = useMemo(() => {
        let total = (valorTotalSabor ?? 0) + (borda?.preco ?? 0) + (massa?.preco ?? 0) + (valorAdicional ?? 0) + valorTotalAdicionais;
        return total;
    }, [sabores, borda, valorAdicional, valorTotalAdicionais])

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!sabores.length) {
            snackbar("Sabores inválidos", { severity: "error" })
            return;
        }

        if (produto.configuracao?.massaObrigatoria && !massa) {
            snackbar("Massa é obrigatória", { severity: "error" });
            return;
        }

        if (produto.configuracao?.bordaObrigatoria && !borda) {
            snackbar("Borda é obrigatória", { severity: "error" });
            return;
        }

        const dadosProduto: IProdutoPedido = {
            uuidProdutoReferencia: produto.uuid,
            valorAdicional,
            ...produto,
            observacao,
            sabores,
            massa: massa ?? undefined,
            borda: borda ?? undefined,
            adicionaisSelecionados: adicionaisSelecionados.length ? adicionaisSelecionados : undefined
        }

        if (uuid) {
            dadosProduto.uuid = uuid;
        }

        onSubmit(dadosProduto);
        handleToggleDrawer();
    }

    useEffect(() => {
        if (bordaEdicao) {
            setBorda(bordaEdicao);
        }

        if (massaEdicao) {
            setMassa(massaEdicao);
        }

        if (saboresEdicao) {
            setSabores(saboresEdicao)
        }

        if (observacaoEdicao) {
            setObservacao(observacaoEdicao);
        }

        if (valorAdicionalEdicao) {
            setValorAdicional(valorAdicionalEdicao);
        }

        if (adicionaisSelecionadosEdicao) {
            setAdicionaisSelecionados(adicionaisSelecionadosEdicao);
        }
    }, [
        valorAdicionalEdicao,
        observacaoEdicao,
        saboresEdicao,
        bordaEdicao,
        adicionaisSelecionadosEdicao
    ])

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={handleToggleDrawer}
            sx={{ borderRadius: 0, zIndex: 10002 }}
            disableRestoreFocus
        >
            <form onSubmit={handleSubmit}>
                <DetalhesContainer>
                    {modeloTela != "desktop" ? (
                        <Button onClick={handleToggleDrawer} sx={{
                            height: "70px",
                            width: '100%',
                            marginBottom: '2rem'
                        }} startIcon={<ChevronLeft />}>Voltar</Button>
                    ) : ''}
                    <HeaderDetalhesContainer>
                        {produto.imagens?.length ? (
                            <Avatar
                                alt="Pizza"
                                src={`${produto.imagens[0].url}`}
                                sx={{ width: 90, height: 90 }}
                            />
                        ) : (
                            <Avatar
                                alt="Pizza"
                                src={`${process.env.PUBLIC_URL}/images/produtos/pizza.png`}
                                sx={{ width: 90, height: 90, objectFit: "contain" }}
                            />
                        )}
                        <div>
                            <Typography gutterBottom variant="h5" fontWeight={"bold"} component="div">
                                {produto.descricao}
                            </Typography>
                            <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                {real(valorTotal)}
                            </Typography>
                        </div>
                    </HeaderDetalhesContainer>
                    <BodyDetalhesContainer>
                        <Grid
                            spacing={1}
                            container
                            height={"100%"}
                        >
                            <Grid item xs={12} sm={6}>
                                <PaperMontagem variant="outlined">
                                    <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                        Montagem
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: "100%",
                                            padding: "1rem",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center"
                                        }}
                                    >
                                        <Stack width={"100%"} flexDirection={"row"} justifyContent={"space-between"}>
                                            <Typography gutterBottom variant="h6" component="div">
                                                Sabores<Typography component="span" variant="body2" color="error" sx={{ ml: 0.5 }}>*</Typography>
                                            </Typography>
                                            <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                {sabores && sabores.length && valorTotalSabor ? real(valorTotalSabor) : "R$0,00"}
                                            </Typography>
                                        </Stack>
                                        {produto.sabores && (
                                            <Autocomplete
                                                multiple
                                                value={sabores}
                                                noOptionsText="Nenhum sabor encontrado"
                                                onChange={handleSaboresChange}
                                                options={produto.sabores?.filter(s => !s.inativadoAs) ?? []}
                                                getOptionLabel={(sabor) => sabor.descricao}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Sabores"
                                                        placeholder="Sabores"
                                                        autoFocus
                                                    />
                                                )}
                                                sx={{ width: '100%', marginTop: "0.5rem" }}
                                                isOptionEqualToValue={(option, value) => false}
                                            />
                                        )}
                                    </Box>

                                    {produto.bordas && produto.bordas.filter(b => !b.inativadoAs).length > 0 && (
                                        <>
                                            <Divider />
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    padding: "1rem",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <Stack width={"100%"} flexDirection={"row"} justifyContent={"space-between"}>
                                                    <Typography gutterBottom variant="h6" component="div">
                                                        Borda{produto.configuracao?.bordaObrigatoria && <Typography component="span" variant="body2" color="error" sx={{ ml: 0.5 }}>*</Typography>}
                                                    </Typography>
                                                    <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                        {borda ? real(borda.preco) : "R$ 0,00"}
                                                    </Typography>
                                                </Stack>
                                                <Autocomplete
                                                    value={borda}
                                                    onChange={handleBordaChange}
                                                    noOptionsText="Nenhuma borda encontrada"
                                                    options={produto.bordas?.filter(b => !b.inativadoAs) ?? []}
                                                    getOptionLabel={(borda) => borda.descricao}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Bordas" placeholder="Bordas" />
                                                    )}
                                                    sx={{ width: '100%', marginTop: "0.5rem" }}
                                                />
                                            </Box>
                                        </>
                                    )}

                                    {produto.massas && produto.massas.filter(m => !m.inativadoAs).length > 0 && (
                                        <>
                                            <Divider />
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    padding: "1rem",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <Stack width={"100%"} flexDirection={"row"} justifyContent={"space-between"}>
                                                    <Typography gutterBottom variant="h6" component="div">
                                                        Massa{produto.configuracao?.massaObrigatoria && <Typography component="span" variant="body2" color="error" sx={{ ml: 0.5 }}>*</Typography>}
                                                    </Typography>
                                                    <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                        {massa ? real(massa.preco) : "R$ 0,00"}
                                                    </Typography>
                                                </Stack>
                                                <Autocomplete
                                                    value={massa}
                                                    onChange={handleMassaChange}
                                                    noOptionsText="Nenhuma massa encontrada"
                                                    options={produto.massas?.filter(m => !m.inativadoAs) ?? []}
                                                    getOptionLabel={(massa) => massa.descricao}
                                                    renderInput={(params) => (
                                                        <TextField {...params} label="Massas" placeholder="Massas" />
                                                    )}
                                                    sx={{ width: '100%', marginTop: "0.5rem" }}
                                                />
                                            </Box>
                                        </>
                                    )}
                                    {produto.adicionais && produto.adicionais.filter(a => !a.inativadoAs).length > 0 && (
                                        <>
                                            <Divider />
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    padding: "1rem",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "center"
                                                }}
                                            >
                                                <Stack width={"100%"} flexDirection={"row"} justifyContent={"space-between"}>
                                                    <Typography gutterBottom variant="h6" component="div">
                                                        Adicionais
                                                        {produto.configuracao?.maxAdicionais !== undefined && (
                                                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                                                ({adicionaisSelecionados.length}/{produto.configuracao.maxAdicionais})
                                                            </Typography>
                                                        )}
                                                    </Typography>
                                                    <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                        {valorTotalAdicionais ? real(valorTotalAdicionais) : "R$ 0,00"}
                                                    </Typography>
                                                </Stack>
                                                <Stack width={"100%"} flexDirection={"row"} flexWrap={"wrap"} gap={1} mt={1}>
                                                    {produto.adicionais.filter(a => !a.inativadoAs).map(adicional => {
                                                        const selecionado = adicionaisSelecionados.some(a => a.uuid === adicional.uuid);
                                                        return (
                                                            <Chip
                                                                key={adicional.uuid}
                                                                label={`${adicional.descricao} (+${real(adicional.preco)})`}
                                                                onClick={() => handleToggleAdicional(adicional)}
                                                                color={selecionado ? "primary" : "default"}
                                                                variant={selecionado ? "filled" : "outlined"}
                                                                clickable={selecionado || !limiteAdicionaisAtingido}
                                                                disabled={!selecionado && limiteAdicionaisAtingido}
                                                            />
                                                        );
                                                    })}
                                                </Stack>
                                            </Box>
                                        </>
                                    )}
                                    <Divider />
                                    <Box
                                        sx={{
                                            width: "100%",
                                            padding: "1rem",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center"
                                        }}
                                    >
                                        <Stack width={"100%"} flexDirection={"row"} justifyContent={"space-between"}>
                                            <Typography gutterBottom variant="h6" component="div">
                                                Observação
                                            </Typography>
                                            <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                {valorAdicional ? real(valorAdicional) : "R$ 0,00"}
                                            </Typography>
                                        </Stack>
                                        <Box sx={{ width: "100%", justifyContent: "center", display: "flex" }}>
                                            <TextField
                                                sx={{ width: '65%' }}
                                                label="Observação"
                                                placeholder="Observação"
                                                onChange={handleObservacao}
                                                value={observacao}
                                            />
                                            <TextField
                                                sx={{ width: '35%', marginLeft: "0.5rem" }}
                                                label="Valor adicional"
                                                placeholder="Valor adicional"
                                                onChange={handleValorAdicional}
                                                value={valorAdicional}
                                                InputProps={{
                                                    inputComponent: RealInput as any,
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </PaperMontagem>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                {modeloTela == "desktop" ? (
                                    <PaperResumo variant="outlined">
                                        <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                            Resumo
                                        </Typography>
                                        <Stack
                                            width={"100%"}
                                            flexDirection={"column"}
                                            justifyContent={"flex-start"}
                                            alignItems={"center"}
                                            height={"100%"}
                                        >
                                            <Paper variant='outlined' sx={{ padding: "1rem", width: "100%" }}>
                                                <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                    Borda
                                                </Typography>
                                                {borda && (
                                                    <>{borda.descricao}</>
                                                )}
                                            </Paper>

                                            <Paper variant='outlined' sx={{ padding: "1rem", width: "100%", marginTop: "1rem" }}>
                                                <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                    Sabores
                                                </Typography>
                                                <Stack sx={{ height: "100%" }} flexDirection={"column"}>
                                                    {sabores && sabores.length >= 1 && sabores.map((sabor, index) => {
                                                        if (sabor.descricao) {
                                                            return <div key={index}>{sabor.descricao}</div>
                                                        }
                                                    })}
                                                </Stack>
                                            </Paper>
                                            <Paper variant='outlined' sx={{ padding: "1rem", width: "100%", marginTop: "1rem" }}>
                                                <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                    Massa
                                                </Typography>
                                                {massa && (
                                                    <>{massa.descricao}</>
                                                )}
                                            </Paper>
                                            {adicionaisSelecionados.length > 0 && (
                                                <Paper variant='outlined' sx={{ padding: "1rem", width: "100%", marginTop: "1rem" }}>
                                                    <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                        Adicionais
                                                    </Typography>
                                                    <Stack sx={{ height: "100%" }} flexDirection={"column"}>
                                                        {adicionaisSelecionados.map(a => (
                                                            <div key={a.uuid}>{a.descricao}</div>
                                                        ))}
                                                    </Stack>
                                                </Paper>
                                            )}
                                            <Paper variant='outlined' sx={{ padding: "1rem", width: "100%", marginTop: "1rem" }}>
                                                <Typography gutterBottom variant="h6" fontWeight={"bold"} component="div">
                                                    Observação
                                                </Typography>
                                                <Stack sx={{ height: "100%" }} flexDirection={"column"}>
                                                    {observacao && <div>{observacao}</div>}
                                                </Stack>
                                            </Paper>
                                            <Button type='submit' variant='contained' sx={{ marginTop: "2rem", width: "100%", height: "90px" }}>
                                                CONCLUIR
                                            </Button>
                                        </Stack>
                                    </PaperResumo>
                                ) : (
                                    <Button type='submit' variant='contained' sx={{ marginTop: "2rem", width: "100%", height: "90px" }}>
                                        CONCLUIR
                                    </Button>
                                )}

                            </Grid>
                        </Grid>
                    </BodyDetalhesContainer>
                </DetalhesContainer>
            </form>
        </Drawer>
    );
}